import {
  Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger,
  Card, CardFooter, CardHeader, Link, makeStyles, shorthands, Text, tokens,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger
} from '@fluentui/react-components';
import { DeleteRegular, EditRegular, MoreHorizontalFilled } from '@fluentui/react-icons';
import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { makeDocsLink } from "@tdp/common/constants";
import { useCommand } from '@tdp/core';

import { createAppZoomHook } from '../../hooks';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(610px, 1fr))',
    gap: '12px',
    margin: '0 12px 0 0'
  },
  gridItem: {
    height: '96px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'row',
  },
  cardIcon: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  innerFlex: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexGrow: 1
  }
});

const useStaticTabs = createAppZoomHook(app => app.staticTabs);
const useConfigurableTabs = createAppZoomHook(app => app.configurableTabs);
const useMeetingExtension = createAppZoomHook(app => app.meetingExtensionDefinition);
const useActivities = createAppZoomHook(app => app.activities);
const useMessagingExtensions = createAppZoomHook(app => app.messagingExtensions);
const useBots = createAppZoomHook(app => app.bots);
const useAppLens = createAppZoomHook((app) => app);


interface IFeatureListItem {
  src: string;
  title: string;
  description: string;
  link: string;
  route: string;
}

const AppFeaturesCards: React.FC<{ isAdded?: boolean }> = ({ isAdded }) => {
  const styles = useStyles();
  const intl = useIntl();
  const navigate = useNavigate();

  const [shouldUpdate, setShouldUpdate] = React.useState(false);
  const [deleteDailog, setDeleteDialog] = React.useState<string>('')

  const appId = useParams<{ appId: string }>().appId ?? '';

  const staticTabs = useStaticTabs(appId);
  const configurableTabs = useConfigurableTabs(appId);
  const meetingExtension = useMeetingExtension(appId);
  const activities = useActivities(appId);
  const messagingExtensions = useMessagingExtensions(appId);
  const bots = useBots(appId);
  const formLens = useAppLens(appId);


  const writeApp = useCommand(
    (commands) => commands.appDetailCommands.writeApp
  );


  const features: IFeatureListItem[] = [
    {
      src: '/images/icons/person.png',
      title: intl.formatMessage({ defaultMessage: 'Personal app', description: 'Title for personal app feature' }),
      description: intl.formatMessage({ defaultMessage: 'A dedicated space or bot to help individual users focus on their own tasks or view activities important to them.', description: 'Description for personal app feature' }),
      link: makeDocsLink("microsoftteams/platform/tabs/how-to/create-personal-tab"),
      route: 'personal-app'
    },
    {
      src: '/images/icons/tabs.png',
      title: intl.formatMessage({ defaultMessage: 'Group and channel app', description: 'Title for group and channel app feature' }),
      description: intl.formatMessage({ defaultMessage: 'A space to display hosted app experiences (such as a list or dashboard) in team channels and group chats.', description: 'Description for group and channel app feature' }),
      link: makeDocsLink("microsoftteams/platform/tabs/how-to/create-channel-group-tab"),
      route: 'channel-and-group-app'
    },
    {
      src: '/images/icons/bot.png',
      title: intl.formatMessage({ defaultMessage: 'Bot', description: 'Title for bot feature' }),
      description: intl.formatMessage({ defaultMessage: 'A conversational UI that can perform a set of tasks, reply to questions, and proactively send notifications in personal and shared contexts.', description: 'Description for bot feature' }),
      link: makeDocsLink("microsoftteams/platform/concepts/bots/bots-overview"),
      route: 'bot'
    },
    {
      src: '/images/icons/message.png',
      title: intl.formatMessage({ defaultMessage: 'Messaging extension', description: 'Title for messaging extension feature' }),
      description: intl.formatMessage({ defaultMessage: 'A shortcut for inserting your app’s content or acting on a message without navigating away from a conversation.', description: 'Description for messaging extension feature' }),
      link: makeDocsLink("microsoftteams/platform/concepts/messaging-extensions"),
      route: 'messaging-extension'
    },
    {
      src: '/images/icons/meeting.png',
      title: intl.formatMessage({ defaultMessage: 'Meeting extension', description: 'Title for meeting extension feature' }),
      description: intl.formatMessage({ defaultMessage: 'Options for integrating your app with the Teams meeting experience, including the meeting stage and chat.', description: 'Description for meeting extension feature' }),
      link: makeDocsLink("microsoftteams/platform/apps-in-teams-meetings/teams-apps-in-meetings"),
      route: 'meeting-app'
    },
    {
      src: '/images/icons/activity.png',
      title: intl.formatMessage({ defaultMessage: 'Activity feed notification', description: 'Title for activity feed notification feature' }),
      description: intl.formatMessage({ defaultMessage: 'Keep users informed and engaged with app notifications in the activity feed.', description: 'Description for activity feed notification feature' }),
      link: makeDocsLink("graph/teams-send-activityfeednotifications"),
      route: 'activity'
    },
  ];

  const addedFeatures: IFeatureListItem[] = []
  const nonAddedFeatures: IFeatureListItem[] = [];
  let finalList: IFeatureListItem[] = [];

  if (staticTabs.current && staticTabs.current.length > 0) {
    addedFeatures.push(features[0]);
  }
  else {
    nonAddedFeatures.push(features[0]);
  }

  if (configurableTabs.current && configurableTabs.current.length > 0) {
    addedFeatures.push(features[1]);
  }
  else {
    nonAddedFeatures.push(features[1]);
  }

  if (bots.current && bots.current.length > 0) {
    addedFeatures.push(features[2]);
  }
  else {
    nonAddedFeatures.push(features[2]);
  }

  if (messagingExtensions.current && messagingExtensions.current.length > 0) {
    addedFeatures.push(features[3]);
  }
  else {
    nonAddedFeatures.push(features[3]);
  }

  if (meetingExtension.current) {
    addedFeatures.push(features[4]);
  }
  else {
    nonAddedFeatures.push(features[4]);
  }

  if (activities.current?.activityTypes?.length > 0) {
    addedFeatures.push(features[5]);
  }
  else {
    nonAddedFeatures.push(features[5]);
  }

  if (isAdded) {
    finalList = addedFeatures
  }
  else {
    finalList = nonAddedFeatures;
  }

  React.useEffect(() => {
    if (shouldUpdate) {
      writeApp({
        appDetails: formLens.current,
      });
      setShouldUpdate(false);
    }
  }, [shouldUpdate, writeApp, formLens]);

  const onCapabilityRemoved = (route: string) => {
    if (route === 'personal-app') {
      staticTabs.update(() => []);
    }
    if (route === 'channel-and-group-app') {
      configurableTabs.update(() => []);
    }
    if (route === 'bot') {
      bots.update(() => []);
    }
    if (route === 'messaging-extension') {
      messagingExtensions.update(() => []);
    }
    if (route === 'meeting-app') {
      meetingExtension.update(() => null);
    }
    if (route === 'activity') {
      activities.update(() => null);
    }
    setShouldUpdate(true);
  }

  const Elipsis: React.FC<{
    route: string;
  }> = ({ route }) => {
    return (
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button icon={<MoreHorizontalFilled />} appearance="transparent" />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<EditRegular />} onClick={() => { navigate(route); }}>
              {intl.formatMessage({ defaultMessage: "Edit" })}
            </MenuItem>
            <MenuItem icon={<DeleteRegular />} onClick={() => { setDeleteDialog(route); }}>
              {intl.formatMessage({ defaultMessage: "Remove" })}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    )
  }

  const Title = () => {
    if (isAdded) {
      return (
        <FormattedMessage
          defaultMessage={"Added features"}
          description={"Heading for features cards section"} />
      )
    }
    return (
      <Text weight="bold" size={400}>
        <FormattedMessage
          defaultMessage={"Select a feature to add"}
          description={"Heading for features cards section"} />
      </Text>
    )
  }

  if (finalList.length === 0) {
    return (<></>)
  }

  const DialogDelete = () => (
    <Dialog open={!!deleteDailog}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{
            intl.formatMessage({ defaultMessage: "Remove feature", description: "Title for remove feature dialog" })
          }</DialogTitle>
          <DialogContent>
            <div>{
              intl.formatMessage({ defaultMessage: "Are you sure you want to remove this feature?", description: "Message for remove feature confirmation dialog" })
            }</div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={() => { setDeleteDialog(''); }}>
                {intl.formatMessage({ defaultMessage: "Cancel", description: "Cancel button text" })}
              </Button>
            </DialogTrigger>
            <Button appearance="primary" onClick={() => { onCapabilityRemoved(deleteDailog); setDeleteDialog(''); }}>
              {intl.formatMessage({ defaultMessage: "Confirm", description: "Confirm button text" })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )

  return (<>
    <Text weight="bold" size={400}>
      <Title />
    </Text>
    <DialogDelete />
    <div className={styles.grid}>
      {finalList.map((feature, index) => (
        <div key={index}>
          <Card className={styles.gridItem} onClick={
            () => {
              navigate(feature.route);
            }
          }>
            <CardHeader
              image={
                <div className={styles.cardIcon}>
                  <img src={feature.src} height={24} width={24} alt='' />
                </div>
              }
            />
            <div className={styles.innerFlex}>
              <div className={styles.cardBody}>
                <Text size={300} weight='bold'>{feature.title}</Text>
                <span>
                  <Text size={200}>{feature.description}</Text>
                  <Link href={feature.link} target='_blank'>
                    <Text size={200} style={{ marginLeft: '6px', textDecoration: 'underline' }}>
                      {intl.formatMessage({ defaultMessage: "Learn more" })}
                    </Text>
                  </Link>
                </span>
              </div>
              {isAdded && <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <Elipsis route={feature.route} />
              </div>
              }
            </div>
          </Card>
        </div>
      ))}
    </div>
  </>);
};

export default AppFeaturesCards;
